import { makeAutoObservable, runInAction } from 'mobx';
import {
  IAuthDataSource,
  UNEXPECTED_ERROR_KEY,
} from '../../../../../infrastructure/types/features/auth/auth-types';
import { SubmittingStatus } from '../../../../../../shared/infrastructure/types/common/common-types';
import { ResponseStatus } from '../../../../../../shared/infrastructure/types/api/api-types';
import { InvitationErrorData } from '../../../../../infrastructure/types/features/auth/auth-contracts';
import { InvitationValidator } from '../validators/InvitationValidator';

class InvitationManager {
  email?: LoginEmail;
  newPassword?: LoginPassword;
  passwordConfirmation?: LoginPassword;
  invitationToken?: string;
  globalError?: string;
  emailServerError?: string;
  passwordServerError?: string;
  passwordConfirmationServerError?: string;

  validator: InvitationValidator;

  constructor(
    private datasource: IAuthDataSource,
    private getFormStatus: () => SubmittingStatus,
    private setFormStatus: (status: SubmittingStatus) => void,
    private autoLogin: (email: LoginEmail, password: LoginPassword) => void,
  ) {
    makeAutoObservable(this, {});
    this.validator = new InvitationValidator(this);
  }

  get formStatus() {
    return this.getFormStatus();
  }

  setNewPassword(password: LoginPassword) {
    this.newPassword = password;
    this.passwordServerError = undefined;
  }

  setPasswordConfirmation(password: LoginPassword) {
    this.passwordConfirmation = password;
    this.passwordConfirmationServerError = undefined;
  }

  initInvitation(token: string, email?: LoginEmail) {
    this.invitationToken = token;
    this.email = email;
    this.clearServerErrors();
    this.setFormStatus(SubmittingStatus.UNTOUCHED);

    this.datasource.validateInvitation(token).then(response => {
      runInAction(() => {
        if (response.status === ResponseStatus.SUCCESS) {
          this.email = response.result.email;
          return;
        }
        if (response.status === ResponseStatus.SERVICE_ERROR) {
          this.globalError =
            (response.error.errorData as InvitationErrorData)?.error ?? UNEXPECTED_ERROR_KEY;
          return;
        }
        this.globalError = UNEXPECTED_ERROR_KEY;
      });
    });
  }

  submit() {
    if (this.validator.hasErrors) {
      return this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
    }

    this.setFormStatus(SubmittingStatus.SUBMITTING);
    this.clearServerErrors();

    this.datasource
      .confirmInvitation(this.invitationToken as string, {
        password: this.newPassword as LoginPassword,
        password_confirmation: this.passwordConfirmation as LoginPassword,
      })
      .then(response => {
        runInAction(() => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.autoLogin(this.email as LoginEmail, this.newPassword as LoginPassword);
            return;
          }

          if (response.status === ResponseStatus.SERVICE_ERROR) {
            this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
            const data = response.error.errorData as InvitationErrorData;
            if (data?.error) {
              this.globalError = data.error;
              return;
            }
            if (data?.errors) {
              this.emailServerError = data.errors.email;
              this.passwordServerError = data.errors.password;
              this.passwordConfirmationServerError = data.errors.password_confirmation;
              return;
            }
            this.globalError = UNEXPECTED_ERROR_KEY;
            return;
          }

          this.setFormStatus(SubmittingStatus.ERROR_SUBMITTING);
          this.globalError = UNEXPECTED_ERROR_KEY;
        });
      });
  }

  private clearServerErrors() {
    this.globalError = undefined;
    this.emailServerError = undefined;
    this.passwordServerError = undefined;
    this.passwordConfirmationServerError = undefined;
  }

  reset() {
    this.email = undefined;
    this.newPassword = undefined;
    this.passwordConfirmation = undefined;
    this.invitationToken = undefined;
    this.clearServerErrors();
    this.setFormStatus(SubmittingStatus.UNTOUCHED);
  }
}

export { InvitationManager };
